// src/pages/tickets/components/TicketFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { useCrearTicket, useActualizarTicket } from '@/hooks/queries/useTickets';
import { useClientes } from '@/hooks/queries/useClientes';
import type { Ticket, TipoTicket, PrioridadTicket } from '@/types';

const schema = z.object({
  titulo: z.string().min(1, 'El titulo es requerido').max(500),
  descripcion: z.string().optional(),
  tipo: z.enum(['INCIDENTE', 'REQUERIMIENTO', 'DUDA']),
  prioridad: z.enum(['CRITICA', 'ALTA', 'MEDIA', 'BAJA']),
  cliente_id: z.string().min(1, 'Seleccione un cliente'),
  fecha_limite: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  ticket?: Ticket;
}

const TIPOS = [
  { value: 'INCIDENTE', label: 'Incidente' },
  { value: 'REQUERIMIENTO', label: 'Requerimiento' },
  { value: 'DUDA', label: 'Duda' },
];

const PRIORIDADES = [
  { value: 'CRITICA', label: 'Critica' },
  { value: 'ALTA', label: 'Alta' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'BAJA', label: 'Baja' },
];

export function TicketFormModal({ open, onClose, ticket }: Props) {
  const isEditing = !!ticket;
  const crearMutation = useCrearTicket();
  const actualizarMutation = useActualizarTicket();
  const { data: clientesData } = useClientes({ page: 1, page_size: 100 });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      titulo: '',
      descripcion: '',
      tipo: 'INCIDENTE',
      prioridad: 'MEDIA',
      cliente_id: '',
      fecha_limite: '',
    },
  });

  useEffect(() => {
    if (ticket) {
      form.reset({
        titulo: ticket.titulo,
        descripcion: ticket.descripcion || '',
        tipo: ticket.tipo,
        prioridad: ticket.prioridad,
        cliente_id: ticket.cliente_id,
        fecha_limite: ticket.fecha_limite || '',
      });
    } else {
      form.reset({
        titulo: '',
        descripcion: '',
        tipo: 'INCIDENTE',
        prioridad: 'MEDIA',
        cliente_id: '',
        fecha_limite: '',
      });
    }
  }, [ticket, form]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing) {
        await actualizarMutation.mutateAsync({ id: ticket.id, data });
      } else {
        await crearMutation.mutateAsync(data);
      }
      form.reset();
      onClose();
    } catch {
      // Error manejado por el hook
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const isLoading = crearMutation.isPending || actualizarMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Ticket' : 'Nuevo Ticket'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">
              Titulo <span className="text-red-500">*</span>
            </Label>
            <Input
              id="titulo"
              {...form.register('titulo')}
              placeholder="Describa brevemente el problema..."
            />
            {form.formState.errors.titulo && (
              <p className="text-sm text-red-500">{form.formState.errors.titulo.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={form.watch('tipo')}
                onValueChange={(value) => form.setValue('tipo', value as TipoTicket)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={form.watch('prioridad')}
                onValueChange={(value) => form.setValue('prioridad', value as PrioridadTicket)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map((prioridad) => (
                    <SelectItem key={prioridad.value} value={prioridad.value}>
                      {prioridad.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cliente_id">
              Cliente <span className="text-red-500">*</span>
            </Label>
            <Select
              value={form.watch('cliente_id')}
              onValueChange={(value) => form.setValue('cliente_id', value)}
            >
              <SelectTrigger error={!!form.formState.errors.cliente_id}>
                <SelectValue placeholder="Seleccione un cliente" />
              </SelectTrigger>
              <SelectContent>
                {clientesData?.items?.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.cliente_id && (
              <p className="text-sm text-red-500">{form.formState.errors.cliente_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="fecha_limite">Fecha Limite</Label>
            <Input id="fecha_limite" type="date" {...form.register('fecha_limite')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              {...form.register('descripcion')}
              placeholder="Describa el problema en detalle..."
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEditing ? 'Guardar' : 'Crear Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
